import React, { useEffect, useState } from "react";
import { Modal as AntdModal, Button, Typography, Tag, Space } from "antd";
import api from "../../../../services/api";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";

const { Text } = Typography;

interface ActivityItem {
    id: number;
    type: string;
    icon: string;
    bgColor: string;
    title: string;
    time: string;
    description?: string;
    acitivityBy?: { name: string; image: string };
}

interface ActivityGroup {
    date: string;
    activities: ActivityItem[];
}

interface LeadActivityModalProps {
    open: boolean;
    lead: { id?: any; key?: any; name?: string; mobile_no?: string } | null;
    signature: string | null;
    onClose: () => void;
}

const LeadActivityModal: React.FC<LeadActivityModalProps> = ({
    open, lead, signature, onClose,
}) => {
    const [activityGroups, setActivityGroups] = useState<ActivityGroup[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && lead) {
            fetchActivities();
        } else {
            setActivityGroups([]);
            setError(null);
        }
    }, [open, lead]);

    const fetchActivities = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await api.post("leadService/getLeadActivities", {
                signature,
                lead_id: lead?.id || lead?.key,
            });
            setActivityGroups(res.data.data.data || []);
        } catch (e) {
            console.error("LeadActivityModal: failed to fetch activities", e);
            setError("Failed to load activities. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const totalActivities = activityGroups.reduce(
        (sum, g) => sum + g.activities.length, 0
    );

    return (
        <AntdModal
            open={open}
            onCancel={onClose}
            centered
            width={620}
            maskClosable={false}
            keyboard={false}
            zIndex={9999}
            getContainer={() => document.body}
            styles={{
                content: {
                    padding: 0, display: "flex", flexDirection: "column",
                    maxHeight: "90vh", overflow: "hidden", borderRadius: 12,
                },
                header: {
                    flexShrink: 0, padding: "16px 24px 12px",
                    borderBottom: "1px solid #f0f0f0", marginBottom: 0,
                },
                body: {
                    flex: 1, overflowY: "auto", overflowX: "hidden",
                    padding: "20px 24px 8px",
                    scrollbarWidth: "thin" as any, scrollbarColor: "#d1d5db transparent",
                },
                footer: {
                    flexShrink: 0, padding: "12px 24px 16px",
                    borderTop: "1px solid #f0f0f0", marginTop: 0,
                },
                mask: {
                    position: "fixed", inset: 0, width: "100vw", height: "100vh",
                    backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                    background: "rgba(0, 0, 0, 0.45)",
                },
            }}
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Space>
                        <span style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                            Lead Activity
                        </span>
                        {lead?.name && (
                            <Tag color="blue" style={{ fontSize: 12 }}>
                                {lead.name}{lead.mobile_no ? ` · ${lead.mobile_no}` : ""}
                            </Tag>
                        )}
                        {totalActivities > 0 && (
                            <span style={{
                                fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 10,
                                background: "#dbeafe", color: "#1e40af", border: "1px solid #bfdbfe",
                            }}>
                                {totalActivities} {totalActivities === 1 ? "activity" : "activities"}
                            </span>
                        )}
                    </Space>
                </div>
            }
            footer={
                <div style={{ textAlign: "right" }}>
                    <Button onClick={onClose}>Close</Button>
                </div>
            }
        >
            {isLoading && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <div className="spinner-border text-primary" role="status" style={{ width: "2rem", height: "2rem" }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p style={{ marginTop: 12, fontSize: 13, color: "#6b7280" }}>Loading activities…</p>
                </div>
            )}

            {!isLoading && error && (
                <div style={{
                    fontSize: 13, color: "#b91c1c", padding: "8px 12px",
                    background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
                }}>
                    {error}
                </div>
            )}

            {!isLoading && !error && activityGroups.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <span style={{ fontSize: 28 }}>📭</span>
                    <p style={{ marginTop: 8, fontSize: 13, color: "#9ca3af" }}>No activities found for this lead</p>
                </div>
            )}

            {/* ── Activity groups ── */}
            {!isLoading && !error && activityGroups.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    {activityGroups.map((group, gi) => (
                        <div key={gi}>
                            <div style={{ marginBottom: 10 }}>
                                <span style={{
                                    display: "inline-flex", alignItems: "center", gap: 5,
                                    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
                                    background: "#ede9fe", color: "#7c3aed", border: "1px solid #ddd6fe",
                                }}>
                                    <i className="ti ti-calendar" style={{ fontSize: 11 }} />
                                    {humanReadableDate(group.date)}
                                </span>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {group.activities.map((activity) => (
                                    <div
                                        key={activity.id}
                                        style={{
                                            border: "1px solid #e5e7eb",
                                            borderRadius: 10,
                                            padding: "12px 14px",
                                            background: "#fff",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            gap: 12,
                                        }}
                                    >

                                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: 1 }}>
                                            <span className={`avatar avatar-sm avatar-rounded flex-shrink-0 ${activity.bgColor}`}
                                                style={{ minWidth: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <i className={`ti ${activity.icon}`} style={{ fontSize: 16 }} />
                                            </span>

                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                                                    <Text strong style={{ fontSize: 13, color: "#111827" }}>
                                                        {activity.title}
                                                    </Text>
                                                    <span style={{
                                                        fontSize: 11, padding: "1px 7px", borderRadius: 8,
                                                        background: "#f3f4f6", color: "#6b7280",
                                                        border: "1px solid #e5e7eb",
                                                    }}>
                                                        {humanReadableDate(activity.time, true, undefined, undefined, false)}
                                                    </span>
                                                </div>

                                                {activity.description && (
                                                    <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 0 0" }}>
                                                        {activity.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {activity.acitivityBy && (
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                                                fontSize: 11, color: "#6b7280",
                                                background: "#f9fafb", border: "1px solid #f3f4f6",
                                                borderRadius: 20, padding: "3px 8px",
                                            }}>
                                                <span className="avatar avatar-xs avatar-rounded"
                                                    style={{ width: 20, height: 20, overflow: "hidden", borderRadius: "50%" }}>
                                                    <ImageWithBasePath
                                                        src={activity.acitivityBy.image || "assets/img/users/user.png"}
                                                        alt="user"
                                                    />
                                                </span>
                                                <span style={{ whiteSpace: "nowrap" }}>{activity.acitivityBy.name}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AntdModal>
    );
};

export default LeadActivityModal;
